import classNames from 'classnames';
import Table from './Table';
import { useMemo, useState } from 'react';
import Pagination from './Pagination';
import { ArrowDownAzIcon, ArrowUpAzIcon, ArrowUpDownIcon } from 'lucide-react';

interface SortableTableHeaderProps {
  key: string;
  label?: string | React.ReactElement;
  className?: string;
  sortable?: boolean;
  sortColumnType?: 'string' | 'number';
  format?: (value: string, row: never, key: string) => string | React.ReactElement;
}

interface SortableTableProps {
  data: never[];
  defaultSortColumn?: string;
  defaultSortDirection?: number;
  headers: SortableTableHeaderProps[];
  className?: string;
  pageSize?: number;
  onRowClick?: (row: never) => void;
}

const getPropValue = (sourceObject: object, dotNotationPath: string): string => {
  let returnData = sourceObject;
  dotNotationPath.split('.').forEach((subPath) => {
    // @ts-expect-error unknown props
    returnData = returnData[subPath];
  });
  return returnData + '';
};

const formatValue = (headerProps: SortableTableHeaderProps, row: never, value: string) => {
  if (headerProps.format) {
    return headerProps.format(value, row, headerProps.key);
  }
  return value;
};

function SortableTable({
  headers,
  data,
  defaultSortColumn,
  defaultSortDirection,
  pageSize = 10,
  className,
  onRowClick,
}: SortableTableProps) {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumnKey, setSortColumnKey] = useState<string>(defaultSortColumn ?? '');
  const [sortColumnDirection, setSortColumnDirection] = useState<number>(defaultSortDirection ?? 1);
  const [sortColumnType, setSortColumnType] = useState<'string' | 'number'>('string');

  const sortedData = useMemo(() => {
    const sortedData = [...(data ?? [])].sort((a, b) => {
      const valA = getPropValue(a, sortColumnKey);
      const valB = getPropValue(b, sortColumnKey);
      if (sortColumnType === 'string') {
        return valA.localeCompare(valB) * sortColumnDirection;
      }
      //@ts-expect-error unknown type
      return (valA - valB) * sortColumnDirection;
    });
    return sortedData;
  }, [data, sortColumnDirection, sortColumnKey, sortColumnType]);

  const paginatedData = useMemo(() => {
    const length = sortedData?.length;
    const start = (currentPage - 1) * pageSize;
    const end = Math.min(start + pageSize, length);
    return sortedData.slice(start, end);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    const totalPages = sortedData.length / pageSize;
    return Math.ceil(totalPages);
  }, [sortedData.length, pageSize]);

  const sortColumn = (
    newSortColumnKey: string,
    newSetSortColumnType: 'string' | 'number' = 'string'
  ) => {
    if (sortColumnKey === newSortColumnKey) {
      setSortColumnDirection(sortColumnDirection * -1);
    } else {
      setSortColumnDirection(1);
    }
    setSortColumnKey(newSortColumnKey);
    setSortColumnType(newSetSortColumnType);
  };
  return (
    <>
      <div>
        Page {Math.min(totalPages, currentPage)} of {totalPages} ({data?.length} records)
      </div>

      <div className="flex justify-center text-sm">
        <Pagination currentPage={currentPage} totalPages={totalPages} setPage={setCurrentPage} />
      </div>
      <Table className={classNames('text-sm', className)}>
        <thead className="">
          <tr>
            {headers.map((headerProps) => (
              <Table.Th
                key={headerProps.key}
                role={headerProps.sortable ? 'button' : ''}
                onClick={
                  headerProps.sortable
                    ? () => sortColumn(headerProps.key, headerProps.sortColumnType)
                    : undefined
                }
              >
                <div className="flex flex-row items-center">
                  {headerProps.sortable && (
                    <span
                      data-sortcolumnkey={headerProps.key}
                      className={classNames(
                        'm-1',
                        sortColumnKey === headerProps.key ? '' : 'text-neutral-400'
                      )}
                    >
                      {sortColumnKey === headerProps.key ? (
                        sortColumnDirection > 0 ? (
                          <ArrowDownAzIcon size={20} />
                        ) : (
                          <ArrowUpAzIcon size={20} />
                        )
                      ) : (
                        <ArrowUpDownIcon size={20} />
                      )}
                    </span>
                  )}
                  {headerProps.label ?? headerProps.key}
                </div>
              </Table.Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((usedSection, index) => {
            return (
              <Table.Tr key={index} className="border-b border-neutral-200 hover:bg-blue-50">
                {headers.map((headerProps) => {
                  const cell = (
                    <Table.Td
                      key={headerProps.key}
                      className={headerProps.className}
                      onClick={() => {
                        if (onRowClick) {
                          onRowClick(usedSection);
                        }
                      }}
                    >
                      {formatValue(
                        headerProps,
                        usedSection,
                        getPropValue(usedSection, headerProps.key)
                      )}
                    </Table.Td>
                  );
                  return cell;
                })}
              </Table.Tr>
            );
          })}
        </tbody>
      </Table>
      <div className="flex justify-center text-sm">
        <Pagination currentPage={currentPage} totalPages={totalPages} setPage={setCurrentPage} />
      </div>
    </>
  );
}

export default SortableTable;
